import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormControl, Select, MenuItem } from '@mui/material';
import { Moon, Sun, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUserDataAuthConsumer } from '../context/UserDataContext';
import { useAirline } from '../context/AirlineContext';
import { fetchAllowedGroups } from '../services/allowedGroupsService';
import { ROUTES, AIRLINE_DISPLAY_NAMES, AIRLINES } from '../constants/appConstants';
import '../styles/TopBar.scss';

interface TopBarProps {
  isAdmin?: boolean;
  brandAirlines?: string[];
  isBeta?: boolean;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
}

const noticeBannerMessage = 'Please avoid using ACARS History 2.0 until further notice and use the legacy ACARS History application as the source of truth until further notice';

const TopBar: React.FC<TopBarProps> = ({ isAdmin = false, brandAirlines = [], isBeta = false, theme, onToggleTheme }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const { airline, setAirline } = useAirline();
  const [canSelectAS, setCanSelectAS] = useState(false);
  const [canSelectQX, setCanSelectQX] = useState(false);
  const [allowedGroupsByAirline, setAllowedGroupsByAirline] = useState<Record<string, string[]>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const userGroups = useAuth();
  const userData = useUserDataAuthConsumer();

  const firstName = userData?.given_name || '';
  const lastName = userData?.family_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'User';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  const userEmail = userData?.email || '';
  const inlineRole = isAdmin ? 'Administrator' : 'User';
  const airlineDisplay = userData?.company
    ? AIRLINE_DISPLAY_NAMES[userData.company] || userData.company
    : '';
  const brandLogo = require('../Assets/alaska_white_name.PNG');
  const subbrandsImg = require('../Assets/Subbrands.png');

  // Fetch allowed groups for airline selection
  useEffect(() => {
    const loadAllowedGroups = async () => {
      try {
        const token = userGroups?.userData?.access_token || '';
        if (token) {
          const groupsData = await fetchAllowedGroups(token);
          setAllowedGroupsByAirline(groupsData);
        }
      } catch (error) {
        console.error('Error loading allowed groups:', error);
      }
    };

    if (userGroups?.userData?.access_token) {
      loadAllowedGroups();
    }
  }, [userGroups?.userData?.access_token]);

  // Check user permissions for airline selection
  useEffect(() => {
    const groups: string[] = (userGroups.userData?.profile?.groups as string[]) || [];
    
    const allowedGroupsAS = allowedGroupsByAirline['AS'] || [];
    const allowedGroupsQX = allowedGroupsByAirline['QX'] || [];

    const userCanSelectAS = groups.some((group) => allowedGroupsAS.includes(group));
    const userCanSelectQX = groups.some((group) => allowedGroupsQX.includes(group));

    const effectiveCanSelectAS = brandAirlines.length > 0 ? brandAirlines.includes('AS') : userCanSelectAS;
    const effectiveCanSelectQX = brandAirlines.length > 0 ? brandAirlines.includes('QX') : userCanSelectQX;

    setCanSelectAS(effectiveCanSelectAS);
    setCanSelectQX(effectiveCanSelectQX);

    // Set default airline based on permissions
    if (!effectiveCanSelectAS && airline === 'AS' && effectiveCanSelectQX) {
      setAirline('QX');
    } else if (effectiveCanSelectAS && airline === '') {
      setAirline('AS');
    }
  }, [userGroups.userData?.profile?.groups, airline, allowedGroupsByAirline, brandAirlines]);

  // Debug logging for admin status
  useEffect(() => {
    //console.log('TopBar - isAdmin prop:', isAdmin);
    //console.log('TopBar - userEmail:', userEmail);
  }, [isAdmin, userEmail]);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  const toggleAdminDropdown = useCallback(() => {
    setAdminDropdownOpen(prev => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = useCallback(async () => {
    setDropdownOpen(false);
    try {
      await signOut();
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [signOut]);

  const handleNavClick = useCallback((route: string) => {
    navigate(route);
  }, [navigate]);

  const isActive = (route: string) => location.pathname === route;

  return (
    <>
    <header className={`topbar ${isBeta ? 'topbar--beta' : ''}`}>
      <div className="topbar-inner">
        {/* Left: Logo & Title & Airline Dropdown */}
        <div className="topbar-left">
          {isBeta ? (
            <div className="topbar-beta-branding">
              <span className="topbar-beta-title">ACARS History 2.0</span>
              <img
                src={subbrandsImg}
                alt="Alaska Hawaiian Horizon"
                className="topbar-beta-subbrands-img"
              />
            </div>
          ) : (
            <>
              <img
                src={brandLogo}
                alt="Alaska Airlines"
                className="topbar-logo"
              />
              <span className="topbar-divider" />
              <span className="topbar-title">ACARS History 2.0</span>
            </>
          )}
          {(location.pathname === ROUTES.OVERVIEW || location.pathname === ROUTES.BETA_OVERVIEW) && (canSelectAS || canSelectQX) && (
            <FormControl className="topbar-airline-dropdown" size="small">
              <Select
                value={airline}
                onChange={(e) => setAirline(e.target.value)}
                className="topbar-airline-select"
                displayEmpty
              >
                {canSelectAS && (
                  <MenuItem value={AIRLINES.AS} className="topbar-airline-menu-item">
                    AS
                  </MenuItem>
                )}
                {canSelectQX && (
                  <MenuItem value={AIRLINES.QX} className="topbar-airline-menu-item">
                    QX
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </div>

        {/* Center: Navigation */}
        <nav className="topbar-nav">
          <button
            type="button"
            className={`topbar-nav-item ${isActive(ROUTES.OVERVIEW) || isActive(ROUTES.BETA_OVERVIEW) ? 'active' : ''}`}
            onClick={() => handleNavClick(isBeta ? ROUTES.BETA_OVERVIEW : ROUTES.OVERVIEW)}
          >
            Overview
          </button>
          {isAdmin && (
            <div className="topbar-admin-dropdown" ref={adminDropdownRef}>
              <button
                type="button"
                className={`topbar-nav-item ${isActive(ROUTES.ADMIN) || isActive(ROUTES.ADMIN_USERS) ? 'active' : ''}`}
                onClick={toggleAdminDropdown}
              >
                Admin
                <svg
                  className={`topbar-admin-chevron ${adminDropdownOpen ? 'open' : ''}`}
                  width="10"
                  height="10"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {adminDropdownOpen && (
                <div className="topbar-admin-submenu">
                  <button
                    type="button"
                    className={`topbar-submenu-item ${isActive(ROUTES.ADMIN) ? 'active' : ''}`}
                    onClick={() => {
                      handleNavClick(ROUTES.ADMIN);
                      setAdminDropdownOpen(false);
                    }}
                  >
                    Allowed Groups
                  </button>
                  <button
                    type="button"
                    className={`topbar-submenu-item ${isActive(ROUTES.ADMIN_USERS) ? 'active' : ''}`}
                    onClick={() => {
                      handleNavClick(ROUTES.ADMIN_USERS);
                      setAdminDropdownOpen(false);
                    }}
                  >
                    Manage Users
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Right: User Info & Dropdown */}
        <div className="topbar-right" ref={dropdownRef}>
          <div className="topbar-user-inline">
            <span className="topbar-username topbar-username--inline">{fullName}</span>
            <span className="topbar-inline-separator" aria-hidden="true">|</span>
            <span className="topbar-user-role-inline">{inlineRole}</span>
            <div className="topbar-avatar" aria-hidden="true">{initials}</div>
          </div>

          {isBeta && theme && onToggleTheme && (
            <button
              type="button"
              className="topbar-theme-toggle"
              onClick={onToggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
            </button>
          )}

          <div
            className="topbar-user"
            onClick={toggleDropdown}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
            aria-label="Open user menu"
          >
            <MoreHorizontal className={`topbar-menu-dots ${dropdownOpen ? 'open' : ''}`} size={18} aria-hidden="true" />
          </div>

          {dropdownOpen && (
            <div className="topbar-dropdown">
              <div className="topbar-dropdown-header">
                <div className="topbar-dropdown-avatar">{initials}</div>
                <div className="topbar-dropdown-info">
                  <span className="topbar-dropdown-name">{fullName}</span>
                  <span className="topbar-dropdown-role">
                    {isAdmin ? 'Administrator' : 'User'}
                    {airlineDisplay ? ` · ${airlineDisplay}` : ''}
                  </span>
                  {userEmail && (
                    <span className="topbar-dropdown-email">{userEmail}</span>
                  )}
                </div>
              </div>
              <div className="topbar-dropdown-divider" />
              <button
                type="button"
                className="topbar-dropdown-item topbar-dropdown-logout"
                onClick={handleLogout}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 14H3.333A1.333 1.333 0 0 1 2 12.667V3.333A1.333 1.333 0 0 1 3.333 2H6M10.667 11.333L14 8l-3.333-3.333M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
    <div className={`topbar-notice ${isBeta ? 'topbar-notice--beta' : ''}`} role="alert">
      <span className="topbar-notice-message">{noticeBannerMessage}</span>
    </div>
    </>
  );
};

export default TopBar;
